import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import usePageMeta from '@/hooks/usePageMeta';
import { media, news } from '@/utils/data';

const TABS = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'news', label: '资讯' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [tab, setTab] = useState('all');
  const [input, setInput] = useState(q);

  const keyword = q.trim().toLowerCase();

  usePageMeta({
    title: q ? `搜索“${q}” - 御宅屋影视平台` : '全站搜索 - 御宅屋影视平台',
    description: q
      ? `御宅屋为你找到与“${q}”相关的电影、电视剧与影视资讯。`
      : '御宅屋全站搜索，快速查找电影、电视剧与影视资讯。',
    keywords: q ? `${q},搜索${q},御宅屋搜索` : '全站搜索,影视搜索,御宅屋',
  });

  const matchMedia = useMemo(() => {
    if (!keyword) return [];
    return media.filter((item) =>
      [item.title, item.enTitle, item.director, item.summary, ...item.genres, ...item.cast, ...item.tags]
        .join(' ')
        .toLowerCase()
        .includes(keyword),
    );
  }, [keyword]);

  const matchNews = useMemo(() => {
    if (!keyword) return [];
    return news.filter((item) =>
      [item.title, item.summary, ...item.content].join(' ').toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const movieResults = matchMedia.filter((m) => m.type === 'movie');
  const tvResults = matchMedia.filter((m) => m.type === 'tv');

  const showMovie = tab === 'all' || tab === 'movie';
  const showTv = tab === 'all' || tab === 'tv';
  const showNews = tab === 'all' || tab === 'news';

  const totalCount =
    (showMovie ? movieResults.length : 0) +
    (showTv ? tvResults.length : 0) +
    (showNews ? matchNews.length : 0);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (!next) return;
    setSearchParams({ q: next });
    setTab('all');
  };

  const noResults = keyword.length > 0 && totalCount === 0;

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb items={[{ label: '搜索结果' }]} />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-900/60 to-background-100 border border-background-200/70 p-6 md:p-8 mb-6">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          {q ? (
            <>
              搜索“<span className="text-accent-400">{q}</span>”的结果
            </>
          ) : (
            '全站搜索'
          )}
        </h1>
        <form onSubmit={handleSearch} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-xl">
          <div className="flex items-center gap-2 bg-background-200/70 border border-background-300/60 rounded-full pl-4 pr-1 py-1.5 flex-1">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              name="q"
              placeholder="输入影视名称、类型、演员或关键词"
              className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-500 flex-1"
            />
            <button
              type="submit"
              className="shrink-0 px-4 py-2 text-sm rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>
        {keyword && (
          <p className="mt-4 text-sm text-foreground-600">
            共找到 <span className="text-accent-400 font-semibold">{totalCount}</span> 条与“{q}”相关的内容
          </p>
        )}
      </div>

      {keyword && totalCount > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => {
            const count =
              t.key === 'all'
                ? movieResults.length + tvResults.length + matchNews.length
                : t.key === 'news'
                  ? matchNews.length
                  : t.key === 'movie'
                    ? movieResults.length
                    : tvResults.length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                  tab === t.key
                    ? 'bg-accent-500 text-background-50 border-accent-500 font-medium'
                    : 'border-background-300/60 text-foreground-700 hover:border-accent-500/50 hover:text-accent-400'
                }`}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {!keyword && (
        <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200/70">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            输入关键词开始搜索
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            试试搜索“科幻”“悬疑”，或直接输入影视名称
          </p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {['科幻', '悬疑', '爱情', '古装'].map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="px-3.5 py-1.5 rounded-full border border-background-300/60 text-sm text-foreground-700 hover:border-accent-500/50 hover:text-accent-400 transition-colors"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      )}

      {noResults && (
        <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200/70">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            没有找到与“{q}”相关的内容
          </h3>
          <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览我们的推荐</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-movie-2-line"></i>
              浏览电影频道
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              回到首页
            </Link>
          </div>
        </div>
      )}

      {keyword && totalCount > 0 && (
        <div className="flex flex-col gap-10">
          {showTv && tvResults.length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                电视剧（{tvResults.length}）
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {tvResults.map((m) => (
                  <MovieCard key={m.id} item={m} />
                ))}
              </div>
            </section>
          )}

          {showMovie && movieResults.length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                电影（{movieResults.length}）
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {movieResults.map((m) => (
                  <MovieCard key={m.id} item={m} />
                ))}
              </div>
            </section>
          )}

          {showNews && matchNews.length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                影视资讯（{matchNews.length}）
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {matchNews.map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/${n.id}`}
                    className="group flex items-center gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors"
                  >
                    <div className="w-24 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} 影视资讯`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                        {n.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{n.summary}</p>
                      <p className="mt-1 text-xs text-foreground-500">
                        <i className="ri-time-line mr-1"></i>
                        {n.publishedAt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}